import { useMemo, useState } from 'react';
import { ArrowLeft, Download, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
    useAuthUtilizationReport,
    useExportAuthUtilization,
    type AuthUtilizationFilter,
    type AuthUtilStatus,
} from '@/api/reports';
import { useClientNames } from '@/api/clients';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
const STATUS_OPTIONS: Array<{ value: AuthUtilStatus; label: string }> = [
    { value: 'Active',   label: 'Active' },
    { value: 'Expiring', label: 'Expiring (≤30d)' },
    { value: 'Expired',  label: 'Expired' },
    { value: 'Pending',  label: 'Pending' },
    { value: 'HighUtil', label: 'High util (≥80%)' },
];

// Lightweight mapping so the per-row badge picks the same palette as the
// client-list page without introducing a new statusBadge variant just for
// this page.
const STATUS_VARIANT: Record<AuthUtilStatus, 'success' | 'warning' | 'error' | 'neutral'> = {
    Active:   'success',
    Expiring: 'warning',
    Expired:  'error',
    Pending:  'neutral',
    HighUtil: 'warning',
};

export function AuthUtilizationPage() {
    const canExport = useAuthStore((s) => s.hasPermission('export_reports'));

    const [filter, setFilter] = useState<AuthUtilizationFilter>({});
    const [draft, setDraft]   = useState<AuthUtilizationFilter>({});

    const { data, isLoading, isError, error, isFetching } = useAuthUtilizationReport(filter);
    const exportM = useExportAuthUtilization();
    const clientsQuery = useClientNames();

    const clientOptions = useMemo(() => {
        if (!clientsQuery.data) return [];
        return clientsQuery.data
            .map((c) => ({ id: c.id, label: `${c.last}, ${c.first}` }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [clientsQuery.data]);

    const payerOptions = useMemo(() => {
        if (!data?.byPayer) return [];
        return data.byPayer
            .map((p) => p.payer)
            .filter((p) => p && p !== 'Unassigned')
            .sort((a, b) => a.localeCompare(b));
    }, [data?.byPayer]);

    const codeOptions = useMemo(() => {
        if (!data?.rows) return [];
        return Array.from(new Set(data.rows.map((r) => r.code).filter(Boolean))).sort();
    }, [data?.rows]);

    function apply() { setFilter({ ...draft }); }
    function clear() { setDraft({}); setFilter({}); }

    async function runExport() {
        try { await exportM.mutateAsync(filter); } catch { /* isError renders below */ }
    }

    return (
        <div className="p-6 max-w-[1400px] mx-auto space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <Link to="/reporting" className="text-xs text-text-subtle hover:text-text inline-flex items-center gap-1 mb-1">
                        <ArrowLeft className="w-3 h-3" /> All reports
                    </Link>
                    <h1 className="font-display text-2xl font-bold text-text">Authorization Utilization</h1>
                    <p className="text-xs text-text-subtle">
                        One row per client authorization. Expiring and high-utilization auths are surfaced at the top so they can be renewed before a claim bounces.
                    </p>
                </div>
                {canExport && (
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={runExport}
                        disabled={exportM.isPending || isLoading}
                    >
                        {exportM.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                        Export CSV
                    </Button>
                )}
            </div>

            <Card>
                <CardBody>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                        <SelectField label="Client" value={draft.clientId} options={clientOptions}
                            onChange={(v) => setDraft({ ...draft, clientId: v })} />
                        <label className="text-xs block">
                            <span className="text-text-muted">Payer</span>
                            <select
                                value={draft.payerName ?? ''}
                                onChange={(e) => setDraft({ ...draft, payerName: e.target.value || undefined })}
                                className="mt-1 h-8 w-full px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                            >
                                <option value="">All payers</option>
                                {payerOptions.map((p) => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </label>
                        <label className="text-xs block">
                            <span className="text-text-muted">Code</span>
                            <select
                                value={draft.code ?? ''}
                                onChange={(e) => setDraft({ ...draft, code: e.target.value || undefined })}
                                className="mt-1 h-8 w-full px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                            >
                                <option value="">All codes</option>
                                {codeOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </label>
                        <label className="text-xs block">
                            <span className="text-text-muted">Status</span>
                            <select
                                value={draft.status ?? ''}
                                onChange={(e) => setDraft({ ...draft, status: (e.target.value || undefined) as AuthUtilStatus | undefined })}
                                className="mt-1 h-8 w-full px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                            >
                                <option value="">All statuses</option>
                                {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                            </select>
                        </label>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                        <Button variant="primary" size="sm" onClick={apply}>Apply</Button>
                        <Button variant="ghost" size="sm" onClick={clear}>Clear</Button>
                        {isFetching && <Loader2 className="w-3.5 h-3.5 animate-spin text-text-subtle" />}
                    </div>
                </CardBody>
            </Card>

            {isError ? (
                <Card>
                    <CardBody>
                        <p className="text-sm text-error font-medium">Couldn't load report</p>
                        <p className="text-xs text-text-subtle mt-1">{extractErrorMessage(error, 'Please try again.')}</p>
                    </CardBody>
                </Card>
            ) : isLoading || !data ? (
                <Card><CardBody><div className="flex items-center gap-2 text-text-subtle text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div></CardBody></Card>
            ) : (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                        <Kpi label="Auths" value={data.kpis.totalAuths.toLocaleString()} />
                        <Kpi label="Active" value={data.kpis.active.toLocaleString()} />
                        <Kpi label="Expiring" value={data.kpis.expiring.toLocaleString()} emphasis={data.kpis.expiring > 0} />
                        <Kpi label="Expired" value={data.kpis.expired.toLocaleString()} emphasis={data.kpis.expired > 0} />
                        <Kpi label="High util" value={data.kpis.highUtil.toLocaleString()} emphasis={data.kpis.highUtil > 0} />
                        <Kpi label="Pending" value={data.kpis.pending.toLocaleString()} />
                    </div>

                    <Card>
                        <CardHeader><CardTitle>Authorizations</CardTitle></CardHeader>
                        <CardBody className="pt-0 overflow-x-auto">
                            {data.rows.length === 0 ? (
                                <p className="text-xs text-text-subtle px-3 py-4">No authorizations match the current filter.</p>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                            <th className="px-3 py-2 font-bold">Client</th>
                                            <th className="px-3 py-2 font-bold">Payer</th>
                                            <th className="px-3 py-2 font-bold">Auth #</th>
                                            <th className="px-3 py-2 font-bold">Code</th>
                                            <th className="px-3 py-2 font-bold text-right">Hours</th>
                                            <th className="px-3 py-2 font-bold text-right">Used</th>
                                            <th className="px-3 py-2 font-bold text-right">Remaining</th>
                                            <th className="px-3 py-2 font-bold text-right">% Used</th>
                                            <th className="px-3 py-2 font-bold">End</th>
                                            <th className="px-3 py-2 font-bold text-right">Days Left</th>
                                            <th className="px-3 py-2 font-bold">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.rows.map((r, idx) => (
                                            <tr key={`${r.client_id}:${r.auth_number}:${idx}`} className="border-b border-border last:border-b-0">
                                                <td className="px-3 py-2">
                                                    <Link to={`/clients/${r.client_id}`} className="hover:underline">{r.client_name}</Link>
                                                </td>
                                                <td className="px-3 py-2">{r.payer}</td>
                                                <td className="px-3 py-2 font-mono text-xs">{r.auth_number || '—'}</td>
                                                <td className="px-3 py-2 font-mono text-xs">{r.code || '—'}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{r.hours}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{r.used}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{r.remaining}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">
                                                    <span className={r.pct_used >= 80 ? 'text-warning-text font-medium' : ''}>
                                                        {r.pct_used.toFixed(0)}%
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2">{r.end_date || '—'}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">
                                                    {r.days_left === null
                                                        ? '—'
                                                        : r.days_left < 0
                                                            ? <span className="text-error">{r.days_left}</span>
                                                            : r.days_left <= 30
                                                                ? <span className="text-warning-text">{r.days_left}</span>
                                                                : r.days_left}
                                                </td>
                                                <td className="px-3 py-2">
                                                    <Badge variant={STATUS_VARIANT[r.status]}>{r.status}</Badge>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </CardBody>
                    </Card>

                    <Card>
                        <CardHeader><CardTitle>By payer</CardTitle></CardHeader>
                        <CardBody className="pt-0">
                            {data.byPayer.length === 0 ? (
                                <p className="text-xs text-text-subtle px-3 py-4">No authorizations.</p>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                            <th className="px-3 py-2 font-bold">Payer</th>
                                            <th className="px-3 py-2 font-bold text-right">Auths</th>
                                            <th className="px-3 py-2 font-bold text-right">Hours</th>
                                            <th className="px-3 py-2 font-bold text-right">Used</th>
                                            <th className="px-3 py-2 font-bold text-right">% Used</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.byPayer.map((p) => {
                                            const pct = p.hours > 0 ? (p.used / p.hours) * 100 : 0;
                                            return (
                                                <tr key={p.payer} className="border-b border-border last:border-b-0">
                                                    <td className="px-3 py-2">{p.payer}</td>
                                                    <td className="px-3 py-2 text-right tabular-nums">{p.count}</td>
                                                    <td className="px-3 py-2 text-right tabular-nums">{p.hours}</td>
                                                    <td className="px-3 py-2 text-right tabular-nums">{p.used}</td>
                                                    <td className="px-3 py-2 text-right tabular-nums">{pct.toFixed(0)}%</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </CardBody>
                    </Card>
                </>
            )}
        </div>
    );
}

function SelectField({
    label, value, options, onChange,
}: {
    label: string;
    value: string | undefined;
    options: { id: string; label: string }[];
    onChange: (v: string | undefined) => void;
}) {
    return (
        <label className="text-xs block">
            <span className="text-text-muted">{label}</span>
            <select
                value={value ?? ''}
                onChange={(e) => onChange(e.target.value || undefined)}
                className="mt-1 h-8 w-full px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
            >
                <option value="">All</option>
                {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
        </label>
    );
}

function Kpi({ label, value, emphasis }: { label: string; value: string; emphasis?: boolean }) {
    return (
        <Card>
            <CardBody>
                <p className="text-2xs uppercase tracking-wider text-text-subtle">{label}</p>
                <p className={`font-display text-xl font-bold mt-1 tabular-nums ${emphasis ? 'text-warning-text' : 'text-text'}`}>{value}</p>
            </CardBody>
        </Card>
    );
}
